import React from 'react';
import { AnalyticsHeatmap } from '../../components/AnalyticsHeatmap';
import { AnalyticsTimelineChart } from '../../components/AnalyticsTimelineChart';
import { Loading } from '../../components/Loading';
import { MainLayout } from '../../components/MainLayout';
import { useAnalyticsDashboard } from '../../utils/Hooks/useAnalyticsDashboard';
import styles from '../../utils/styles/pages/Analytics/style.module.css';

function formatMetric(value) {
  return new Intl.NumberFormat('fr-FR').format(value || 0);
}

function StatCard({ label, value, note }) {
  return (
    <article className={styles.statCard}>
      <span className={styles.statLabel}>{label}</span>
      <strong className={styles.statValue}>{value}</strong>
      {note ? <span className={styles.statNote}>{note}</span> : null}
    </article>
  );
}

function RankedList({ items, labelKey, valueKey, emptyLabel }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className={styles.emptyList}>{emptyLabel}</p>;
  }

  return (
    <div className={styles.rankedList}>
      {items.map((item) => (
        <div className={styles.rankedRow} key={item[labelKey]}>
          <span className={styles.rankedLabel}>{item[labelKey]}</span>
          <strong className={styles.rankedValue}>{formatMetric(item[valueKey])}</strong>
        </div>
      ))}
    </div>
  );
}

function Analytics() {
  const { dashboard, loading, error, refreshDashboard } = useAnalyticsDashboard(30);

  const navItems = [
    { id: 'nav-home', label: "Retour à l'ouverture", onClick: () => { window.location.hash = '/'; } },
    { id: 'nav-studio', label: 'Studio', onClick: () => { window.location.hash = '/studio'; } },
    { id: 'nav-refresh', label: 'Rafraîchir', onClick: refreshDashboard }
  ];

  return (
    <MainLayout
      eyebrow="Analytics"
      navItems={navItems}
      subtitle="Visualisation first-party des visites, interactions et zones chaudes du site."
      title="Pilotage des visites."
    >
      <div className={styles.page}>
        {loading ? <Loading label="Chargement du tableau de bord analytics..." /> : null}
        {error && !dashboard ? <p className={styles.error}>Le tableau de bord analytics n’a pas pu etre charge.</p> : null}

        {dashboard ? (
          <>
            <section className={styles.statsGrid}>
              <StatCard label="Pages vues" note="sur 30 jours" value={formatMetric(dashboard.summary.pageViews)} />
              <StatCard label="Sessions" note="visiteurs uniques de session" value={formatMetric(dashboard.summary.uniqueSessions)} />
              <StatCard label="Clics" note="interactions tracees" value={formatMetric(dashboard.summary.clicks)} />
              <StatCard label="Sections vues" note="engagement de scroll" value={formatMetric(dashboard.summary.sectionViews)} />
              <StatCard label="Scroll moyen" note="profondeur max par session" value={`${dashboard.summary.averageScrollDepth || 0}%`} />
            </section>

            <section className={styles.panel}>
              <div className={styles.panelHeader}>
                <span className={styles.kicker}>Trafic</span>
                <h2 className={styles.panelTitle}>Evolution des visites.</h2>
              </div>
              <div className={styles.timeline}>
                <AnalyticsTimelineChart items={dashboard.timeline || []} />
              </div>
            </section>

            <section className={styles.splitGrid}>
              <article className={styles.panel}>
                <div className={styles.panelHeader}>
                  <span className={styles.kicker}>Pages</span>
                  <h2 className={styles.panelTitle}>Pages les plus consultees.</h2>
                </div>
                <RankedList emptyLabel="Aucune page vue pour le moment." items={dashboard.pages} labelKey="route" valueKey="views" />
              </article>

              <article className={styles.panel}>
                <div className={styles.panelHeader}>
                  <span className={styles.kicker}>Sources</span>
                  <h2 className={styles.panelTitle}>Origine du trafic.</h2>
                </div>
                <RankedList emptyLabel="Aucune source detectee pour le moment." items={dashboard.sources} labelKey="source" valueKey="visits" />
              </article>
            </section>

            <section className={styles.splitGrid}>
              <article className={styles.panel}>
                <div className={styles.panelHeader}>
                  <span className={styles.kicker}>Sections</span>
                  <h2 className={styles.panelTitle}>Sections les plus vues.</h2>
                </div>
                <RankedList emptyLabel="Aucune section vue pour le moment." items={dashboard.sections} labelKey="sectionId" valueKey="views" />
              </article>

              <article className={styles.panel}>
                <div className={styles.panelHeader}>
                  <span className={styles.kicker}>CTA</span>
                  <h2 className={styles.panelTitle}>Interactions les plus actives.</h2>
                </div>
                <RankedList emptyLabel="Aucun clic trace pour le moment." items={dashboard.ctas} labelKey="label" valueKey="clicks" />
              </article>
            </section>

            <section className={styles.heatmapGrid}>
              <AnalyticsHeatmap
                heatmaps={dashboard.heatmaps?.home}
                note="Lecture comparée desktop et mobile, réunies dans une seule surface analytics premium avec overlay calibré sur le viewport réel des visiteurs."
                previewPath="/#/?analytics-preview=1"
                title="Home"
              />
            </section>
          </>
        ) : null}
      </div>
    </MainLayout>
  );
}

export default Analytics;
